/**
 * The history of one cell of a table that is kept here, read from the journal.
 *
 * A block's entries are read when a cell of it is first looked at and kept for
 * a short while, so passing the pointer over a run of cells asks once. The cell
 * whose history is open is the one the side panel shows.
 */

import { defineStore } from 'pinia'
import { ref, shallowRef } from 'vue'

import { blockHistory } from '@/api/journal'
import type { JournalEntry } from '@/domain/journal'
import { cellTrail, type CellEvent, type CellTarget } from '@/features/versions/cellTrail'
import { useDraftsStore } from '@/stores/drafts'

/** How long what was read stays good: the journal moves as people save. */
const FRESH_MS = 20_000

/** The most entries read for one block; older ones are in the block's own history. */
const MOST = 100

export interface OpenCell {
  readonly sectionId: string
  readonly target: CellTarget
}

export const useCellHistoryStore = defineStore('cellHistory', () => {
  const drafts = useDraftsStore()

  const read = shallowRef<ReadonlyMap<string, readonly JournalEntry[]>>(new Map())
  const readAt = new Map<string, number>()
  const loading = ref(false)
  const open = ref<OpenCell | null>(null)

  /** The entries of a block, newest first; read again when what is held has gone stale. */
  async function entriesOf(sectionId: string): Promise<readonly JournalEntry[]> {
    const id = drafts.open?.id
    if (id === undefined) return []

    const held = read.value.get(sectionId)
    if (held && Date.now() - (readAt.get(sectionId) ?? 0) < FRESH_MS) return held

    loading.value = true
    try {
      const found = await blockHistory(id, sectionId, { limit: MOST })
      readAt.set(sectionId, Date.now())
      read.value = new Map(read.value).set(sectionId, found)
      return found
    } catch {
      // The tip and the panel say less rather than fail: what is held, or nothing.
      return held ?? []
    } finally {
      loading.value = false
    }
  }

  /** The changes of a cell, the newest first, from what has been read of its block. */
  function trailOf(sectionId: string, target: CellTarget): readonly CellEvent[] {
    return cellTrail(read.value.get(sectionId) ?? [], target)
  }

  /** Whether the block's entries reach as far back as the journal keeps them. */
  function reachedTheStart(sectionId: string): boolean {
    return (read.value.get(sectionId)?.length ?? 0) < MOST
  }

  async function show(cell: OpenCell): Promise<void> {
    open.value = cell
    await entriesOf(cell.sectionId)
  }

  function close(): void {
    open.value = null
  }

  /** Another report is opening: nothing read belongs to it. */
  function reset(): void {
    read.value = new Map()
    readAt.clear()
    open.value = null
  }

  return { open, loading, entriesOf, trailOf, reachedTheStart, show, close, reset }
})
